import type { SliderSchema } from './registry';

// ---- Generic Slider ----
export interface SliderCallbacks {
  onChange: (value: number) => void;
}

// TASK 28 (2026-09-30, Nam's decision): default is MANUAL, not real-time.
//
// Four reasons this is the right default, not a downgrade:
//   1. "Real-time" was an illusion - every preview takes >= 3.1 s (measured on the lightest
//      module), so nothing was actually real-time and there is no speed to lose.
//   2. The toggle sits in the sidebar at 9px, so most users would never touch it. The DEFAULT
//      is the only lever that actually reduces load.
//   3. Predictability: in Manual, what is on screen is what was committed. In real-time the
//      screen can lag behind the slider mid-drag. For a 3D-printing tool where the user then
//      exports, "what I see is what I print" matters more than the feeling of speed.
//   4. The risk (user drags, sees nothing change, assumes the app is broken) is handled by
//      three separate signals - the floating Update button, the "Changes not applied" line,
//      and the segmented switch below - not by the default.
let globalSliderMode = false;  // true = real-time, false = manual

export type AppQuality = 'low' | 'medium' | 'high';
let appQuality: AppQuality = (() => {
  try {
    const stored = localStorage.getItem('boxed3d_app_quality');
    return stored === 'low' || stored === 'medium' || stored === 'high' ? stored : 'low';
  } catch { return 'low'; }
})();
const qualityListeners: Array<(quality: AppQuality) => void> = [];

export function getAppQuality(): AppQuality { return appQuality; }
export function onAppQualityChange(cb: (quality: AppQuality) => void): void { qualityListeners.push(cb); }
export function setAppQuality(quality: AppQuality): void {
  if (appQuality === quality) return;
  appQuality = quality;
  try { localStorage.setItem('boxed3d_app_quality', quality); } catch { /* private mode */ }
  qualityListeners.forEach((cb) => { try { cb(quality); } catch (err) { console.error('[quality] listener failed', err); } });
}
export function setAppQualityTriangleCount(quality: AppQuality, count: number): void {
  const button = document.querySelector<HTMLButtonElement>(`[data-quality="${quality}"]`);
  const detail = button?.querySelector('small');
  if (button) button.dataset.actual = `${count.toLocaleString()} triangles`;
  if (detail && getAppQuality() === quality) detail.textContent = `${count.toLocaleString()} triangles`;
}

export function getGlobalSliderMode(): boolean {
  return globalSliderMode;
}

// TASK 28 (2026-09-30): listeners so the app can react when the user flips between Real-time
// and Manual. Needed because switching back to Real-time should apply anything that was still
// pending - otherwise the toggle appears to do nothing and the pending values never reach the
// server.
const sliderModeListeners: Array<(mode: boolean) => void> = [];

/** Subscribe to Real-time <-> Manual mode changes. */
export function onSliderModeChange(cb: (mode: boolean) => void): void {
  sliderModeListeners.push(cb);
}

export function setGlobalSliderMode(mode: boolean): void {
  if (globalSliderMode === mode) return;
  globalSliderMode = mode;
  for (const cb of sliderModeListeners) {
    try { cb(mode); } catch (err) { console.error('[sliderMode] listener failed', err); }
  }
}

export function createSlider(schema: SliderSchema, initialValue: number, cb: SliderCallbacks): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'control-group';
  wrap.style.cssText = 'margin-bottom: 10px; width: 100%;';

  const label = document.createElement('label');
  label.textContent = `${schema.label}`;
  label.style.cssText = 'font-size:11px;color:var(--text-muted);display:block;margin-bottom:6px;';
  wrap.appendChild(label);

  // Container: slider 3/4 + input 1/4
  const container = document.createElement('div');
  container.style.cssText = 'display:flex;gap:6px;align-items:center;width:100%;';

  const stepVal = String(schema.step ?? 1);

  const input = document.createElement('input');
  input.type = 'range';
  input.min = String(schema.min);
  input.max = String(schema.max);
  input.step = stepVal;
  input.value = String(initialValue);
  input.style.cssText = 'flex:3; min-width: 0;';
  container.appendChild(input);

  const numberInput = document.createElement('input');
  numberInput.type = 'number';
  numberInput.min = String(schema.min);
  numberInput.max = String(schema.max);
  numberInput.step = stepVal;
  numberInput.value = String(initialValue);
  numberInput.style.cssText = `
    flex:1;
    min-width: 0;
    padding: 4px 6px;
    font-size: 11px;
    border: 1px solid var(--border);
    border-radius: 3px;
    font-family: var(--mono-font);
    text-align: center;
  `;
  
  numberInput.addEventListener('focus', () => {
    numberInput.select();
  });
  
  container.appendChild(numberInput);
  wrap.appendChild(container);

  // Update button (shown only in manual mode)
  const updateButton = document.createElement('div');
  updateButton.style.cssText = 'display:none;gap:4px;margin-top:6px;';

  const applyBtn = document.createElement('button');
  applyBtn.textContent = 'Update';
  applyBtn.style.cssText = `
    flex: 1;
    padding: 4px 8px;
    font-size: 10px;
    border: 1px solid var(--accent);
    background: var(--accent);
    color: white;
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.2s ease;
  `;
  applyBtn.addEventListener('mouseover', () => {
    applyBtn.style.opacity = '0.8';
  });
  applyBtn.addEventListener('mouseout', () => {
    applyBtn.style.opacity = '1';
  });

  updateButton.appendChild(applyBtn);

  const resetBtn = document.createElement('button');
  resetBtn.textContent = 'Reset';
  resetBtn.style.cssText = `
    flex: 1;
    padding: 4px 8px;
    font-size: 10px;
    border: 1px solid var(--border);
    background: var(--bg-input);
    color: var(--text-muted);
    border-radius: 3px;
    cursor: pointer;
    transition: all 0.2s ease;
  `;
  resetBtn.addEventListener('mouseover', () => {
    resetBtn.style.opacity = '0.8';
  });
  resetBtn.addEventListener('mouseout', () => {
    resetBtn.style.opacity = '1';
  });

  updateButton.appendChild(resetBtn);
  wrap.appendChild(updateButton);

  const updateUI = (v: number, triggerCallback: boolean = true) => {
    if (isNaN(v)) v = schema.default;
    v = Math.max(schema.min, Math.min(schema.max, v));
    if (schema.integerOnly || (schema.step && schema.step >= 1 && schema.step % 1 === 0)) {
      v = Math.round(v);
    } else if (schema.step && schema.step > 0) {
      v = Math.round(v / schema.step) * schema.step;
      const decimals = (String(schema.step).split('.')[1] || '').length;
      v = parseFloat(v.toFixed(decimals));
    }
    input.value = String(v);
    numberInput.value = String(v);
    if (triggerCallback) {
      cb.onChange(v);
    }
  };

  const updateButtonVisibility = () => {
    const isRealtime = globalSliderMode;
    // TASK 28 (2026-09-30): the per-slider UPDATE button is hidden - one floating Update now
    // applies every pending change (see main.ts). Hiding the whole `updateButton` row was wrong
    // because Reset lives in the same row, so this also removed the per-slider Reset. Only the
    // Update button is hidden; Reset stays available in both modes.
    applyBtn.style.display = 'none';

    // TASK 28: the slider is draggable in BOTH modes now. Manual mode used to lock it
    // (`input.disabled = true`), which forced typing numbers into a tiny box - the opposite of
    // "drag the slider freely, then apply once".
    input.disabled = false;
    numberInput.disabled = false;  // Enter commits, in both modes

    input.style.opacity = '1';
    input.style.cursor = 'pointer';
    numberInput.style.opacity = '1';
    numberInput.style.cursor = 'text';
  };

  input.addEventListener('input', () => {
    const v = parseFloat(input.value);
    numberInput.value = String(v);
    // TASK 28 fix (2026-09-30): this used to be gated on `if (globalSliderMode)`.
    //
    // That gate was written for the OLD manual mode, where a locked slider needed no callback
    // and the per-slider Update button did the work. Once manual mode changed to "drag freely,
    // apply once", keeping the gate broke the whole feature: in Manual mode the slider fired
    // NO callback, so the app never learned anything had changed, so the single floating Update
    // button never appeared - leaving the user with no way to apply their edits at all.
    //
    // The callback now always fires. The APP decides what to do with it (preview immediately in
    // Real-time, just mark pending in Manual), which is the right place for that decision -
    // the slider should not be the thing enforcing the mode.
    updateUI(v, true);
  });

  numberInput.addEventListener('input', () => {
    const v = parseFloat(numberInput.value);
    if (!isNaN(v)) {
      input.value = String(v);
      // Manual mode only records the pending key, so the Update button can
      // appear while typing without sending one preview per character.
      if (!globalSliderMode) cb.onChange(v);
    }
  });

  // Commit number-field edits when focus leaves the field, not only on Enter.
  numberInput.addEventListener('change', () => {
    updateUI(parseFloat(numberInput.value), true);
  });

  // TASK 28 (2026-09-30): Enter commits the typed value. This matches how people expect a
  // number field to behave, and in Manual mode it is the natural way to apply one change.
  numberInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      numberInput.blur();
    }
  });

  applyBtn.addEventListener('click', () => {
    // Only in manual mode: Update button applies the change
    updateUI(parseFloat(numberInput.value), true);
  });

  resetBtn.addEventListener('click', () => {
    // TASK 28 (2026-09-30): this used to pass `false`, so it moved the slider back to the
    // default WITHOUT telling the app - the number on screen said one thing and the model on
    // screen was still the old one. The global "Reset All" always applied its change, so the
    // two Reset buttons disagreed with each other. Now both apply.
    updateUI(schema.default, true);
  });

  // Create observer to monitor global mode changes
  const modeObserver = {
    update: updateButtonVisibility,
  };

  // Store observer on wrap for later cleanup
  (wrap as any)._modeObserver = modeObserver;

  updateButtonVisibility();
  return wrap;
}

// ---- Generic Button ----
export function createButton(text: string, onClick: () => void): HTMLElement {
  const btn = document.createElement('button');
  btn.textContent = text;
  btn.addEventListener('click', onClick);
  return btn;
}
